import React, { useState, useEffect, useCallback } from "react";
import styles from "./PasoTest.module.css";
import Swal from 'sweetalert2';
import { FaTrash, FaEdit, FaEye, FaSadCry,
    FaBrain,
    FaMagic,
    FaRocket,
    FaDog,
    FaCat,
    FaBook,
    FaMusic,
    FaPuzzlePiece,
    } from 'react-icons/fa';
import {useAuth} from '../../Auth/AuthContext'; 
const iconMap = {
    FaTrash: FaTrash,
    FaEdit: FaEdit,
    FaEye: FaEye,
    FaSadCry: FaSadCry,
    FaBrain: FaBrain,
    FaMagic: FaMagic,
    FaRocket: FaRocket,
    FaDog: FaDog,
    FaCat: FaCat,
    FaBook: FaBook,
    FaMusic: FaMusic,
    FaPuzzlePiece: FaPuzzlePiece,
};

const PasoTest = ({ abrirModal, cerrarModal, pasoId, onAgregarTest, onTestActualizado }) => {
    const [tests, setTests] = useState([]);
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testSeleccionado, setTestSeleccionado] = useState(null);
    const [enviando, setEnviando] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (abrirModal) {
            fetchTests();
        }
    }, [abrirModal]);

    const fetchTests = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/obtener-test/${pasoId}`, {
                headers: { 
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status}`);
            }

            const data = await response.json();
            setTests(data);
            setLoading(false);
        } catch (err) {
            console.error('Error:', err);
            setError('Error al cargar los tests. Por favor, intenta de nuevo más tarde.');
            setLoading(false);
        }
    };
    
    const seleccionarTest = useCallback((testId) => {
        setTestSeleccionado(testId);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault(); 

        if(!testSeleccionado){
            setError('Por favor, selecciona un test.');
            return;
        }

        try{
            setEnviando(true);
            setError('');

            const response = await fetch(`${process.env.REACT_APP_API_URL}/pasos/paso-en-test/${pasoId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    testId: testSeleccionado
                })
            });
            
            const result = await response.json();
            
            if (!response.ok) {
                if (response.status === 409) {
                    Swal.fire({
                        title: '¡Test ya asignado!',
                        text: 'Este test ya está asignado a este paso.',
                        icon: 'info',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#242b57',
                        customClass: {
                            popup: 'popupbonito',
                        },
                    });
                } else if (response.status === 400) {
                    Swal.fire({
                        title: 'Error de validación',
                        text: result.error || 'El paso o test especificado no existe.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                        confirmButtonColor: '#242b57',
                        customClass: {
                            popup: 'popupbonito',
                        }
                    });
                } else {
                    Swal.fire({
                        title: 'Error',
                        text: result.error || 'Error al agregar el test al paso.',
                        icon: 'error',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#242b57',
                        customClass: {
                            popup: 'popupbonito',
                        },
                    });
                }
                throw new Error(result.error || 'Error al agregar el test al paso');
            }            
            // Encontrar el test completo para pasarlo al callback
            const testCompleto = tests.find(t => t.te_clave === testSeleccionado);
            
            // Llamar al callback del componente padre
            if (onAgregarTest) {
                onAgregarTest({
                    pasoId,
                    testConf: testCompleto,
                    relacionId: result.id
                });
            }

            // Notificar al componente padre que se actualizó un test
            if (onTestActualizado) {
                onTestActualizado();
            }

            await Swal.fire({
                title: '¡Éxito!',
                text: 'Test agregado correctamente al paso',
                icon: 'success',
                showConfirmButton: false,
                customClass: {
                    popup: 'popupbonito',
                },
                timer: 2000,
            });

            cerrarModalAnim();
            
            // Resetear el formulario
            setTestSeleccionado(null);
            setError(null);

        } catch (err) {
            console.error('Error al agregar test:', err);
            
            if (err.message.includes('ya existe')) {
                setError('Este test ya está asignado a este paso.');
            } else if (err.message.includes('no existe')) {
                setError('El paso o test especificado no existe.');
            } else {
                setError(err.message || 'Error al agregar el test. Intenta de nuevo.');
            }
        } finally {
            setEnviando(false);
        }
    };

    const cerrarModalAnim = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            setTestSeleccionado(null);
            setError(null);
            cerrarModal();
        }, 300);
    };

    const handleCerrarModal = () => {
        cerrarModalAnim();
    };

    const cerrarModalClick = (e) => {
        if (e.target === e.currentTarget) {
            cerrarModalAnim();
        }
    };

    if (!abrirModal) return null;

    return (
        <div className={styles.modalTest} onClick={cerrarModalClick}>
            <div className={`${styles.modalTestCont} ${isClosing ? styles.modalFadeOut : styles.modalAniadirAnim}`}>
                <div className={styles.modalScrollableTest}>
                    <button
                        type="button"
                        className={styles.closeBtnTest}
                        onClick={handleCerrarModal}
                    >
                        &times;
                    </button>
                    <h2 className={styles.tituloTest}>Agrega un test disponible</h2>

                    {error && (
                        <div className={styles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div>
                        {loading ? (
                            <div className={styles.loadingContainer}>
                                <div className={styles.loadingContent}>
                                    <div className={styles.loadingSpinner}></div>
                                    <p className={styles.loadingText}>Cargando sección...</p>
                                </div>
                            </div>
                        ) : tests.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div>
                            <FaSadCry className={styles.emptyIcon} />
                            </div>
                            <h3>No hay tests disponibles</h3>
                            <p>Agrega un primer test :D</p>
                        </div>
                        ) : (
                        <div className={styles.testsGrid}>
                            {tests.map((test) => {
                                const isSelected = testSeleccionado === test.te_clave;
                                return(
                                    <div key={test.te_clave} 
                                    className={`${styles.testCard} ${isSelected ? styles.selected : ''}`}
                                    onClick={(e)=>{
                                        e.stopPropagation();
                                        seleccionarTest(test.te_clave);}}
                                    >
                                        <div className={styles.cardIcon}>
                                        {iconMap[test.te_icon] ? (
                                            React.createElement(iconMap[test.te_icon], { className: styles.customIcon})
                                        ) : (
                                            <FaBrain className={styles.customIcon}></FaBrain>
                                        )}
                                        </div>
                        
                                        <div className={styles.cardContent}>
                                        <h3 className={styles.cardTitle}>{test.te_nombre}</h3>
                                        <p className={styles.cardDescription}>{test.te_descripcion}</p>
                        
                                        <div className={styles.cardInfo}>
                                            {test.te_tiempo_estimado && test.te_tiempo_estimado !== 0 && test.te_tiempo_estimado !== '0' && (
                                            <div className={styles.infoItem}>
                                                <span>{test.te_tiempo_estimado} min</span>
                                            </div>
                                            )}
                                        </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                    <div className={styles.modalActions}>
                        <button
                            type="button"
                            className={styles.botonGuardTest}
                            onClick={handleSubmit}
                            disabled={!testSeleccionado || enviando}
                        >
                            {enviando ? 'Agregando...' : 'Agregar Test'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PasoTest;